/**
 * Pro Default Slider - Global initialization
 *
 * Handles all Swiper slider initialization and video inline playback
 * for the pro-default-slider section. Safe to load once in theme.liquid;
 * works with multiple slider instances on the same page.
 *
 * Optional desktop loop mode (per-instance, opt-in):
 *   When data-loop-desktop="true" is set on the .pds-swiper element AND
 *   the viewport is >= the breakpoint (data-loop-desktop-bp, default 1200px),
 *   the slider runs in infinite-loop mode with the previous slide peeking
 *   from the left. Sliders without this attribute are completely unaffected.
 *
 * Dependencies: Swiper library (swiper-bundle.min.js)
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════
     HELPERS
  ═══════════════════════════════════════════════════════════════ */

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  /* ═══════════════════════════════════════════════════════════════
     SWIPER INITIALIZATION
  ═══════════════════════════════════════════════════════════════ */

  /**
   * Initialize a single Swiper instance.
   * @param {HTMLElement} container - The .pds-swiper element
   */
  function initSlider(container) {
    if (typeof Swiper === 'undefined') {
      console.warn('[pro-default-slider] Swiper not found. Add swiper-bundle.min.js to theme.liquid.');
      return;
    }

    // Prevent double-initialization
    if (container.swiper) return;

    /* ── Read data attributes ──────────────────────────────────── */
    var sectionId   = container.dataset.sectionId;
    var gapMobile   = parseInt(container.dataset.gapMobile,   10) || 16;
    var gapDesktop  = parseInt(container.dataset.gapDesktop,  10) || 24;
    var slidesDesk  = parseFloat(container.dataset.slidesDesktop) || 2.5;
    var slideWidth  = parseInt(container.dataset.slideWidth,  10) || 0;
    var showPag     = container.dataset.showPagination === 'true';
    var showNav     = container.dataset.showNavigation === 'true';
    var showCounter = container.dataset.showCounter    === 'true';

    // ── Desktop loop options (opt-in — absent = standard behaviour) ──
    var loopDesktopEnabled = container.dataset.loopDesktop   === 'true';
    var loopDesktopBp      = parseInt(container.dataset.loopDesktopBp,  10) || 1200;
    var loopPeekWidth      = parseInt(container.dataset.loopPeekWidth,  10) || 160;

    /* ── Fixed-width mode ──────────────────────────────────────── */
    var useFixedWidth = slideWidth > 0;
    if (useFixedWidth) {
      container.style.setProperty('--pds-slide-width', slideWidth + 'px');
    }

    /* ── Evaluate loop state ───────────────────────────────────── */
    function isDesktopLoopActive() {
      return loopDesktopEnabled && window.innerWidth >= loopDesktopBp;
    }

    var desktopLoop = isDesktopLoopActive();

    /* ── Pagination element ────────────────────────────────────── */
    var paginationEl = showPag
      ? document.getElementById('pagination-' + sectionId)
      : null;

    /* ─────────────────────────────────────────────────────────────
       Build Swiper config
    ───────────────────────────────────────────────────────────── */
    function buildConfig(isLoop) {
      var cfg = {
        slidesPerView : useFixedWidth ? 'auto' : 1.2,
        spaceBetween  : gapMobile,
        centeredSlides: false,
        a11y: {
          enabled                : true,
          prevSlideMessage       : 'Previous slide',
          nextSlideMessage       : 'Next slide',
          paginationBulletMessage: 'Go to slide {{index}}',
        },
        keyboard: { enabled: true, onlyInViewport: true },
      };

      if (isLoop) {
        /* ── Desktop loop: infinite with left-peek ─────────────── */
        cfg.loop               = true;
        cfg.loopedSlides       = 1;            // clone 1 slide each side
        cfg.slidesOffsetBefore = loopPeekWidth; // reveals the clone on the left
        cfg.slidesOffsetAfter  = gapDesktop;
        cfg.spaceBetween       = gapDesktop;
        cfg.slidesPerView      = useFixedWidth ? 'auto' : slidesDesk;
        // No breakpoints — we are guaranteed to be at desktop width here
      } else {
        /* ── Standard: responsive, no loop ────────────────────── */
        cfg.loop              = false;
        cfg.slidesOffsetAfter = 16;
        cfg.breakpoints = useFixedWidth ? {
          640: { spaceBetween: gapMobile  },
          768: { spaceBetween: gapDesktop },
          992: { spaceBetween: gapDesktop },
        } : {
          640: { slidesPerView: 1.5,       spaceBetween: gapMobile  },
          768: { slidesPerView: 2,          spaceBetween: gapDesktop },
          992: { slidesPerView: slidesDesk, spaceBetween: gapDesktop },
        };
      }

      /* ── Pagination ──────────────────────────────────────────── */
      if (showPag && paginationEl) {
        cfg.pagination = {
          el            : paginationEl,
          clickable     : true,
          dynamicBullets: false,
        };
      }

      /* ── Navigation arrows ───────────────────────────────────── */
      if (showNav) {
        var prevEl = container.querySelector('.swiper-button-prev');
        var nextEl = container.querySelector('.swiper-button-next');
        if (prevEl && nextEl) {
          cfg.navigation = { prevEl: prevEl, nextEl: nextEl };
        }
      }

      return cfg;
    }

    /* ── Capture real slide count BEFORE Swiper adds loop clones ── */
    // After new Swiper() the wrapper gains extra clone nodes, so count now.
    var realSlideCount = container.querySelectorAll('.swiper-slide').length;

    /* ── Init Swiper ───────────────────────────────────────────── */
    var swiper = new Swiper(container, buildConfig(desktopLoop));

    /* ─────────────────────────────────────────────────────────────
       Counter helpers
    ───────────────────────────────────────────────────────────── */

    /**
     * Loop mode  : realIndex is 0-based from the first real slide and is
     *              clamped to realSlideCount so it never overflows.
     * Standard mode: prefer the active pagination bullet index (fixed-width
     *              sliders can show partial slides that offset realIndex).
     */
    function getDisplayIndex() {
      if (desktopLoop) {
        return (swiper.realIndex % realSlideCount) + 1;
      }
      if (useFixedWidth && swiper.pagination && swiper.pagination.bullets && swiper.pagination.bullets.length) {
        var activeBullet = container.querySelector('.swiper-pagination-bullet-active');
        if (activeBullet) {
          var idx = Array.from(swiper.pagination.bullets).indexOf(activeBullet);
          if (idx >= 0) return idx + 1;
        }
      }
      return swiper.realIndex + 1;
    }

    function getTotalSlides() {
      if (desktopLoop) {
        // Always use the pre-clone count — immune to Swiper internals
        return realSlideCount;
      }
      if (useFixedWidth && swiper.pagination && swiper.pagination.bullets && swiper.pagination.bullets.length) {
        return swiper.pagination.bullets.length;
      }
      return swiper.slides.length;
    }

    function renderCounter(el) {
      el.innerHTML =
        '<span class="pds-counter-current">'  + pad(getDisplayIndex()) + '</span>' +
        '<span class="pds-counter-separator"> / </span>'                            +
        '<span class="pds-counter-total">'    + pad(getTotalSlides())  + '</span>';
    }

    /* ── Wire up slide counter ─────────────────────────────────── */
    if (showCounter) {
      var counter = container.querySelector('.pds-slide-counter');
      if (counter) {
        var updateCounter = function () { renderCounter(counter); };
        updateCounter();
        swiper.on('slideChange',      updateCounter);
        swiper.on('resize',           updateCounter);
        if (swiper.pagination) {
          swiper.on('paginationRender', updateCounter);
          swiper.on('paginationUpdate', updateCounter);
        }
      }
    }

    /* ── Desktop header nav / counter (outside swiper) ─────────── */
    var desktopPrev    = document.querySelector('.pds-desktop-button-prev');
    var desktopNext    = document.querySelector('.pds-desktop-button-next');
    var desktopCounter = document.querySelector('.pds-desktop-counter');

    if (desktopPrev && desktopNext) {
      desktopPrev.addEventListener('click', function () { swiper.slidePrev(); });
      desktopNext.addEventListener('click', function () { swiper.slideNext(); });

      var updateDesktopNavState = function () {
        // Loop mode: arrows always active; standard mode: disable at edges
        desktopPrev.disabled = !desktopLoop && swiper.isBeginning;
        desktopNext.disabled = !desktopLoop && swiper.isEnd;
      };
      updateDesktopNavState();
      swiper.on('slideChange', updateDesktopNavState);
    }

    if (desktopCounter) {
      var updateDC = function () { renderCounter(desktopCounter); };
      updateDC();
      swiper.on('slideChange', updateDC);
      swiper.on('resize',      updateDC);
      if (swiper.pagination) {
        swiper.on('paginationRender', updateDC);
        swiper.on('paginationUpdate', updateDC);
      }
    }

    /* ─────────────────────────────────────────────────────────────
       Resize handler — only for loop-enabled sliders
       Re-inits when viewport crosses the loop breakpoint so the
       correct config (loop vs standard) is always active.
       Sliders without loop opted-in never attach this listener.
    ───────────────────────────────────────────────────────────── */
    if (loopDesktopEnabled) {
      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          var nowLoop = isDesktopLoopActive();
          if (nowLoop !== desktopLoop) {
            desktopLoop = nowLoop;
            swiper.destroy(true, true);
            delete container.swiper; // clear guard so initSlider can re-run
            initSlider(container);
          }
        }, 250);
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     INIT ALL SLIDERS ON PAGE
  ═══════════════════════════════════════════════════════════════ */

  function initAllSliders() {
    document.querySelectorAll('.pds-swiper').forEach(function (container) {
      if (container.classList.contains('pds-swiper--lazy-init') && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              initSlider(entry.target);
              observer.unobserve(entry.target);
            }
          });
        }, { rootMargin: '200px' });
        observer.observe(container);
      } else {
        initSlider(container);
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════════
     VIDEO INLINE PLAYBACK
  ═══════════════════════════════════════════════════════════════ */

  function activateVideo(trigger) {
    var card   = trigger.closest('.pds-card--video');
    var player = card && card.querySelector('.pds-video-player');
    if (!player) return;

    var type    = player.dataset.videoType;
    var videoId = player.dataset.videoId;

    trigger.hidden = true;
    player.hidden  = false;
    player.removeAttribute('aria-hidden');

    if (type === 'shopify') {
      var videoEl = player.querySelector('video');
      if (videoEl) videoEl.play();
      return;
    }

    if (!player.querySelector('iframe')) {
      var src = '';
      if (type === 'youtube') {
        src = 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0';
      } else if (type === 'vimeo') {
        src = 'https://player.vimeo.com/video/' + videoId + '?autoplay=1';
      }
      if (src) {
        player.innerHTML = '<iframe src="' + src + '" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen class="pds-video-iframe"></iframe>';
      }
    }
  }

  function handleVideoClick(e) {
    var trigger = e.target.closest('.pds-video-trigger');
    if (trigger) activateVideo(trigger);
  }

  function handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      var trigger = e.target.closest('.pds-video-trigger');
      if (trigger) { e.preventDefault(); activateVideo(trigger); }
    }
  }

  /* ═══════════════════════════════════════════════════════════════
     BOOT
  ═══════════════════════════════════════════════════════════════ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSliders);
  } else {
    initAllSliders();
  }

  document.addEventListener('click',   handleVideoClick);
  document.addEventListener('keydown', handleKeydown);

  // Expose for Shopify theme editor live preview
  window.ProDefaultSlider = {
    init      : initAllSliders,
    initSingle: initSlider,
  };

})();
